import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import onClickOutside from 'react-onclickoutside'
import { IconExclamationCircle } from '@tabler/icons-react'

import { selectIsCrawling } from 'containers/SiteAudit/selectors'
import ModalDialog from 'components/ui/modal-dialog'
import UiText from 'components/ui/text'
import Button from 'components/ui/button'
import { EVENTS, trackEvent } from 'utils/tracking/events-tracker'

import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import { translate } from '../../utils/translation'
import Icon, { ArrowIcon, DomainIcon } from '../icon/Icon'
import { getMaxValuesForUser } from '../../utils/freeVersionBlock'
import { Text } from '../topography'
import pencilOrange from '../../styles/assets/pencil_orange.svg'
import pencilGray from '../../styles/assets/pencil_gray.svg'
import addOrange from '../../styles/assets/add_orange.svg'
import { selectExtraHeaderHeight } from '../../state/selectors'
import { MIXPANEL_PROPS } from '../../utils/mixpanel'

const ProjectSelectorWrapper = styled.div((props) => ({
  marginLeft: '10px',
  cursor: 'pointer',
  zIndex: 105,
  background: palette.white,
  [mediaQueries.smallDesktop]: {
    fontSize: '20px',
    margin: 0,
  },
  ...props.overridingStyle,
}))

const SelectedProject = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  width: props.fullWidth ? '100%' : '224px',
  height: '65px',
  borderRadius: '2px',
  padding: '0 20px',
  justifyContent: 'space-between',
  backgroundColor: props.selected || props.open ? palette.orange : 'inherit',
  borderBottom: props.open ? 'none' : `1px solid ${palette.lightGray50}`,
  [mediaQueries.smallDesktop]: {
    width: '100%',
  },
}))

const ProjectWrapper = styled.div((props) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '13px 20px',
  background: props.isSelected ? palette.lightGray20 : 'inherit',
  minWidth: '224px',
  height: '65px',
  '&:hover': {
    background: palette.lightGray20,
  },
  '&:hover div': {
    color: palette.orange,
  },
}))

const ProjectTitleWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
})

const ProjectDomainWrapper = styled.div({
  width: 'auto',
  maxWidth: '300px',
  overflow: 'hidden',
  [mediaQueries.mobile]: {
    width: '180px',
  },
})

const ProjectTitle = styled.div((props) => ({
  fontSize: '14px',
  fontFamily: fonts.primaryBook,
  whiteSpace: 'nowrap',
  color: props.isSelected ? palette.orange : 'inherit',
  fontWeight: props.isSelected ? 'bold' : 'inherit',
}))

const ProjectDomain = styled.span(() => ({
  marginTop: '5px',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  fontFamily: fonts.primaryLight,
  color: palette.gray,
}))

const ProjectList = styled.div((props) => ({
  position: 'fixed',
  width: 'auto',
  top: `calc(60px + ${
    props.extraHeaderHeight ? props.extraHeaderHeight : 0
  }px)`,
  left: '220px',
  marginTop: 0,
  zIndex: 105,
  background: palette.white,
  boxShadow: shadows.shadowA,
  maxHeight: '325px',
  overflow: 'auto',

  /* width */
  '::-webkit-scrollbar': {
    width: '4px',
  },

  /* Track */
  '::-webkit-scrollbar-track': {
    background: palette.lightGray,
  },

  /* Handle */
  '::-webkit-scrollbar-thumb': {
    width: '8px',
    background: palette.gray,
  },

  /* Handle on hover */
  '::-webkit-scrollbar-thumb:hover': {
    background: palette.darkGray50,
  },

  [mediaQueries.smallDesktop]: {
    position: 'initial',
    marginTop: '4px',
    [props.align]: 0,
    width: '100%',
    // width:
    //   props.align === 'right' ? 'calc(100vw - 30px)' : 'calc(100vw - 120px)',
  },
}))

const AddButton = styled.div({
  height: '54px',
  display: 'flex',
  alignItems: 'center',
  borderTop: `1px solid ${palette.lightGray}`,
  color: palette.darkGray,
  fontFamily: fonts.primaryBook,
  fontSize: '14px',
  lineHeight: '16px',
  padding: '12px 16px',
})

const SortIconWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const Project = ({
  onClick,
  isSelected,
  icon,
  name,
  domain,
  hoverIndex,
  index,
  onEditProject,
  setHoverIndex,
}) => (
  <ProjectWrapper onClick={onClick} isSelected={isSelected}>
    <ProjectTitleWrapper>
      {icon || (
        <DomainIcon colorIndex={index} style={{ margin: '0 16px 0 0' }}>
          {domain ? domain.charAt(0) : name.charAt(0)}
        </DomainIcon>
      )}

      <ProjectDomainWrapper>
        <ProjectTitle isSelected={isSelected}>{name}</ProjectTitle>
        <ProjectDomain>{domain}</ProjectDomain>
      </ProjectDomainWrapper>
    </ProjectTitleWrapper>

    <Icon
      src={hoverIndex === index ? pencilOrange : pencilGray}
      margin="0 5px"
      onClick={onEditProject}
      onMouseEnter={() => setHoverIndex(index)}
      onMouseLeave={() => setHoverIndex(-1)}
      onTouchStart={() => setHoverIndex(index)}
    />
  </ProjectWrapper>
)

class SidebarProjectSelector extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
      hoverIndex: -1,
      cancelRecrawl: {
        isOpen: false,
        project: null,
      },
    }
  }
  // eslint-disable-next-line
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  openList = () => {
    this.setState((prevState) => ({ isOpen: !prevState.isOpen }))
  }

  selectProject = (project) => {
    const { isCrawling } = this.props
    if (isCrawling) {
      this.setState({
        cancelRecrawl: {
          isOpen: true,
          project,
        },
      })
      return
    }
    this.setState({ isOpen: false })
    this.props.onChangeProject(project)
  }

  editProject = (e, project) => {
    e.stopPropagation()
    this.setState({ isOpen: false, hoverIndex: -1 })
    this.props.onEditProject(project)
  }

  addProject = (eventProps) => {
    this.setState({ isOpen: false })
    this.props.onAddProject(eventProps)
  }

  setHoverIndex = (hoverIndex) => {
    this.setState({ hoverIndex })
  }

  onReclawPause = () => {
    const { cancelRecrawl } = this.state
    sessionStorage.removeItem('site_audit_domain')
    sessionStorage.removeItem('site_audit_mode')
    window.history.pushState({}, '', window.location.pathname)
    this.props.onChangeProject(cancelRecrawl.project)
    this.setState({
      isOpen: false,
      cancelRecrawl: {
        isOpen: false,
        project: null,
      },
    })
  }

  onReclawCancel = () => {
    this.setState({
      cancelRecrawl: {
        isOpen: false,
        project: null,
      },
    })
  }

  render() {
    const { isOpen, hoverIndex, cancelRecrawl } = this.state
    const {
      projects,
      selectedProject,
      fullWidth,
      smallBox,
      extraHeaderHeight,
      hideAddButton,
      align,
      style,
    } = this.props
    const { MAX_PROJECTS_PER_USER } = getMaxValuesForUser()
    const projectsCount = projects ? projects.length : 0
    return (
      <>
        <ModalDialog
          variant="center"
          isOpen={!!(cancelRecrawl.isOpen && cancelRecrawl.project)}
          maxSize="tw-max-w-2xl"
          closeButton={false}
          onClose={() => {
            return null
          }}
          icon={
            <IconExclamationCircle size={48} className="tw-stroke-orange" />
          }
          title="pause_recrawl_heading"
          titleClassName="tw-text-3xl"
          content={
            <UiText className="tw-text-lg tw-text-zinc-700">
              pause_recrawl_subheading
            </UiText>
          }
          footer={
            <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row-reverse">
              <Button
                size="xl"
                onClick={this.onReclawPause}
                className="tw-w-full tw-bg-orange-500 tw-text-lg"
              >
                {translate('pause_recrawl_heading')}
              </Button>
              <Button
                onClick={this.onReclawCancel}
                variant="legacy-outlined"
                className="tw-w-full tw-text-lg"
              >
                cancel
              </Button>
            </div>
          }
        />
        <ProjectSelectorWrapper
          overridingStyle={style}
          data-testid="projectSelector"
        >
          <SelectedProject
            onClick={() => {
              if (projectsCount > 0) {
                this.openList()
              } else {
                trackEvent({
                  event: EVENTS.PROJECT_CREATION_CLICK,
                  mixpanelProperties: [
                    MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
                      .SIDEBAR_TOP,
                  ],
                })
                this.addProject({
                  addProjectButtonLocation:
                    MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
                      .SIDEBAR_TOP,
                })
              }
            }}
            open={isOpen}
            fullWidth={fullWidth}
          >
            <Text
              lineHeight="16px"
              style={{
                display: 'flex',
                alignItems: 'center',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontFamily: fonts.primaryMedium,
                fontSize: '16px',
                lineHeight: '18px',
                color: isOpen ? palette.white : palette.darkGray,
                userSelect: 'none',
              }}
            >
              {projectsCount > 0 && selectedProject
                ? selectedProject.title
                : translate('add_project_button')}
            </Text>
            {projectsCount === 0 && <Icon src={addOrange} margin="0" />}
            {projectsCount > 0 && (
              <SortIconWrapper style={{ marginRight: '10px' }}>
                <ArrowIcon
                  up
                  color={isOpen ? palette.white : palette.darkGray30}
                  width="2px"
                  style={{ marginBottom: '2px' }}
                />
                <ArrowIcon
                  down
                  color={isOpen ? palette.white : palette.darkGray30}
                  width="2px"
                  style={{ marginTop: '2px' }}
                />
              </SortIconWrapper>
            )}
          </SelectedProject>
          {isOpen && (
            <ProjectList
              data-testid="selectedProjectSelector"
              align={align}
              fullWidth={fullWidth}
              smallBox={smallBox}
              extraHeaderHeight={extraHeaderHeight}
            >
              {projects.map((project, index) => (
                <Project
                  index={index}
                  isSelected={project.id === selectedProject?.id}
                  key={project.id}
                  name={project.title}
                  domain={`${translate('project')} | ${project.domain}`}
                  icon={project.icon}
                  onClick={() => this.selectProject(project, index)}
                  onEditProject={(e) => this.editProject(e, project)}
                  setHoverIndex={this.setHoverIndex}
                  hoverIndex={hoverIndex}
                />
              ))}
              {!hideAddButton && (
                <AddButton
                  onClick={() => {
                    trackEvent({
                      event: EVENTS.PROJECT_CREATION_CLICK,
                      mixpanelProperties: [
                        MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
                          .SIDEBAR_PROJECT_SELECTOR,
                      ],
                    })
                    this.addProject({
                      addProjectButtonLocation:
                        MIXPANEL_PROPS.ADD_PROJECT_BUTTON_LOCATION.values
                          .SIDEBAR_PROJECT_SELECTOR,
                    })
                  }}
                >
                  <Icon src={addOrange} margin="0 12px 0 0" />
                  {translate('add_project_button')}
                  <span
                    style={{ fontFamily: fonts.primary, marginLeft: '4px' }}
                  >
                    {`(${projects.length}/${MAX_PROJECTS_PER_USER})`}
                  </span>
                </AddButton>
              )}
            </ProjectList>
          )}
        </ProjectSelectorWrapper>
      </>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  extraHeaderHeight: selectExtraHeaderHeight(),
  isCrawling: selectIsCrawling(),
})

const mapDispatchToProps = () => ({})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(onClickOutside(SidebarProjectSelector))
